import { FormControlLabel, InputAdornment, MenuItem, Select } from '@material-ui/core';
import NumberInput from 'components/Input/NumberInput';
import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { WasteAmount, FoodwastePeriod } from 'redux/ducks/settings';
import { NumberFormatValues } from 'react-number-format';
import { useIntl } from 'react-intl';
import { isNumeric } from 'utils/math';
import { isPeriodMissing } from 'pages/Settings/components/settings/goals/targetValidation';
import { MassUnit, transformAmount } from 'utils/number-format';

const useStyles = makeStyles({
  root: {
    border: 'none',
    display: 'flex'
  },
  form: {
    margin: 0
  },
  formGrow: {
    flex: 1,
    margin: 0
  },
  formLabel: {
    marginRight: 'auto',
    fontWeight: 600
  },
  formLabelInline: {
    margin: '0 12px'
  },
  formInput: {
    maxWidth: '100px'
  },
  selectPeriod: {
    width: '90px'
  },
  selectPeriodWrapper: {
    width: '120px'
  }
});

export interface FoodwasteFieldProps {
  name?: string;
  label: string;
  value: WasteAmount;
  disabled?: boolean;
  onChange: (value: WasteAmount) => void;
  as?: MassUnit;
}

type OwnProps = FoodwasteFieldProps;

const Periods = ['day', 'week', 'month', 'year'] as const;

const FoodwasteField: React.FunctionComponent<OwnProps> = (props) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const { label, name, value, onChange, as = 'g', disabled } = props;
  const amountValue = isNumeric(value.amount)
    ? transformAmount(value.amount, { unit: value.unit, as })
    : '';
  // Amount entered without a period cannot be saved (PROD-14967); flag the period field.
  const periodMissing = isPeriodMissing(value);

  const handleWasteChange = (values: NumberFormatValues) => {
    const { floatValue } = values;
    onChange({
      ...value,
      amount: transformAmount(floatValue, { unit: as, as: value.unit })
    });
  };
  const handlePeriodChange = (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { value: period } = e.target;
    onChange({ ...value, period: period as FoodwastePeriod });
  };

  return (
    <fieldset className={classes.root}>
      <FormControlLabel
        classes={{
          root: classes.formGrow,
          label: classes.formLabel
        }}
        labelPlacement='start'
        control={
          <NumberInput
            disabled={disabled}
            className={classes.formInput}
            decimalScale={2}
            allowNegative={false}
            name={name}
            value={amountValue}
            InputProps={{
              endAdornment: <InputAdornment position='end'>{as}</InputAdornment>
            }}
            onValueChange={handleWasteChange}
          />
        }
        label={label}
      />

      <div className={classes.selectPeriodWrapper}>
        {value.period !== 'fixed' && (
          <FormControlLabel
            classes={{
              root: classes.form,
              label: classes.formLabelInline
            }}
            labelPlacement='start'
            control={
              <Select
                disabled={disabled}
                error={periodMissing}
                // MUI warns and flips the inner input from uncontrolled to
                // controlled when the value is undefined, which is a reachable
                // state: an amount saved without a period (PROD-14967)
                value={value.period ?? ''}
                onChange={handlePeriodChange}
                className={classes.selectPeriod}
              >
                {Periods.map((period) => (
                  <MenuItem key={period} value={period}>
                    {intl.formatMessage({ id: `report.filter_${period}` })}
                  </MenuItem>
                ))}
              </Select>
            }
            label='/'
          />
        )}
      </div>
    </fieldset>
  );
};
export default FoodwasteField;
